import { Image } from "@city-sage/aurum-avis-labs-components";
import { Post, PostPostableTypeEnum } from "@city-sage/core-client";
import { formatDistanceToNow } from "date-fns";
import { Link } from "@tanstack/react-router";
import { Avatar } from "../Avatar";
import { useReactions } from "../../lib/useReactions";
import { ReportButton } from "./ReportButton";
import { LikeButton } from "./LikeButton";
import { CommentButton } from "./CommentButton";

interface PostCardProps {
  post: Post;
  onLike?: (postId: string) => void;
  onComment?: (postId: string) => void;
} 

export function PostCard({ post, onLike, onComment }: PostCardProps) {
  const { isLiked, likeCount, toggleLike, isPending } = useReactions({
    reactableType: 'post',
    reactableId: post.id
  });

  // Ensure we're working with the actual current time vs post creation time
  const timeAgo = formatDistanceToNow(post.createdAt, { addSuffix: true });

  return (
    <div className="bg-white border-b border-gray-100 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <Link 
          to="/users/$userId" 
          params={{ userId: post.account.id }}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <Avatar className="h-8 w-8" account={post.account} />
          <div>
            <p className="font-medium text-sm">{post.account?.userName || 'Anonymous'}</p>
            <p className="text-xs text-gray-500">{timeAgo}</p>
          </div>
        </Link>
      </div>

      {/* Content */}
      {post.text && (
        <p className="text-gray-800 mb-3 leading-relaxed">{post.text}</p>
      )}

      {/* Image */}
      {post.imageUrl && (
        <div className="mb-3 rounded-lg overflow-hidden">
          <Image
            src={post.imageUrl}
            alt="Post image"
            className="w-full h-auto max-h-96 object-cover"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-6 pt-2">
        <LikeButton
          isLiked={isLiked}
          likeCount={likeCount}
          onToggleLike={toggleLike}
          isPending={isPending}
          theme="light"
          layout="horizontal"
        />
        
        <div className="ml-auto">
          <ReportButton
            contentId={post.id}
            contentType="comment"
            theme="light"
            layout="horizontal"
          />
        </div>
      </div>
    </div>
  );
} 